import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { partnerDocumentsApi } from '../../api/partner-documents';
import { ApiError } from '../../api/client';
import { DataTable } from '../../components/DataTable';
import { IconActionButton } from '../../components/IconActionButton';
import { Check, X, RefreshCw, History } from 'lucide-react';
import { useToast } from '../../components/Toast';
import { formatDateTime } from '../../utils/formatDate';

function fileToDataUri(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

interface UploadForm { documentCategory: string; title: string; documentNumber: string; issueDate: string; expiryDate: string; issuingAuthority: string; file: FileList; }

/** Document Management (spec section 17) - real upload, verify/reject, and "replace" that creates a genuine new version rather than overwriting history. */
export function PartnerDocumentsTab({ partnerId }: { partnerId: number }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [replacingId, setReplacingId] = useState<number | null>(null);
  const documentsQuery = useQuery({ queryKey: ['partner-documents', partnerId], queryFn: () => partnerDocumentsApi.list(partnerId) });
  const { register, handleSubmit, reset, setValue } = useForm<UploadForm>();
  const { register: registerReplace, handleSubmit: handleSubmitReplace, setValue: setReplaceValue } = useForm<UploadForm>();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['partner-documents', partnerId] });

  const uploadMutation = useMutation({
    mutationFn: async (values: UploadForm) => {
      const file = values.file[0];
      const fileDataUri = await fileToDataUri(file);
      return partnerDocumentsApi.upload(partnerId, { ...values, file: undefined, fileDataUri, originalFilename: file.name });
    },
    onSuccess: () => { showSuccess('Document uploaded.'); invalidate(); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to upload document.'),
  });

  const replaceMutation = useMutation({
    mutationFn: async (values: UploadForm) => {
      const file = values.file[0];
      const fileDataUri = await fileToDataUri(file);
      return partnerDocumentsApi.replace(replacingId as number, { ...values, file: undefined, fileDataUri, originalFilename: file.name });
    },
    onSuccess: () => { showSuccess('Document replaced with a new version.'); invalidate(); setReplacingId(null); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to replace document.'),
  });

  const verifyMutation = useMutation({ mutationFn: (id: number) => partnerDocumentsApi.verify(id), onSuccess: () => { showSuccess('Verified.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const rejectMutation = useMutation({
    mutationFn: (id: number) => { const reason = window.prompt('Reason for rejection:'); if (!reason) throw new Error('cancelled'); return partnerDocumentsApi.reject(id, reason); },
    onSuccess: () => { showSuccess('Rejected.'); invalidate(); },
    onError: (err) => { if (err instanceof Error && err.message === 'cancelled') return; showError(err instanceof ApiError ? err.message : 'Failed.'); },
  });

  return (
    <div className="panel">
      <div className="panel-body">
        {documentsQuery.data && (
          <DataTable
            rows={documentsQuery.data.data}
            rowKey={(d) => d.id}
            emptyMessage="No documents on file yet."
            columns={[
              { header: 'Title', render: (d) => <a href={partnerDocumentsApi.downloadUrl(d.id)}>{d.title}</a> },
              { header: 'Category', render: (d) => d.document_category },
              { header: 'Version', render: (d) => `v${d.version}` },
              { header: 'Expiry', render: (d) => d.expiry_date ?? '-' },
              { header: 'Status', render: (d) => d.status },
              { header: 'Confidential', render: (d) => Number(d.is_confidential) === 1 ? 'Yes' : 'No' },
              {
                header: '', render: (d) => (
                  <span className="row-actions">
                    {d.status !== 'verified' && <IconActionButton icon={Check} label="Verify" onClick={() => verifyMutation.mutate(d.id)} />}
                    {d.status !== 'rejected' && <IconActionButton icon={X} label="Reject" danger onClick={() => rejectMutation.mutate(d.id)} />}
                    <IconActionButton icon={RefreshCw} label="Replace" onClick={() => setReplacingId(d.id)} />
                    <IconActionButton icon={History} label="Version History" onClick={() => partnerDocumentsApi.getVersionHistory(d.id).then((r) => alert(r.data.map((v) => `v${v.version} - ${v.status} - ${formatDateTime(v.created_at)}`).join('\n')))} />
                  </span>
                ),
              },
            ]}
          />
        )}

        {replacingId && (
          <form className="inline-form" onSubmit={handleSubmitReplace((values) => replaceMutation.mutate(values))} style={{ borderTop: '0.5px solid var(--border)', paddingTop: 8, marginTop: 8 }}>
            <p>Replacing document #{replacingId} - this creates a new version, the old one is kept as history.</p>
            <label>Category<input {...registerReplace('documentCategory', { required: true })} /></label>
            <label>Title<input {...registerReplace('title', { required: true })} /></label>
            <label>File<input type="file" onChange={(e) => setReplaceValue('file', e.target.files as FileList)} /></label>
            <button type="submit" disabled={replaceMutation.isPending}>{replaceMutation.isPending ? 'Replacing…' : 'Upload New Version'}</button>
            <button type="button" onClick={() => setReplacingId(null)}>Cancel</button>
          </form>
        )}

        <h3>Upload Document</h3>
        <form className="inline-form" onSubmit={handleSubmit((values) => uploadMutation.mutate(values))}>
          <label>Category<input {...register('documentCategory', { required: true })} placeholder="e.g. registration_certificate" /></label>
          <label>Title<input {...register('title', { required: true })} /></label>
          <label>Document Number<input {...register('documentNumber')} /></label>
          <label>Issue Date<input type="date" {...register('issueDate')} /></label>
          <label>Expiry Date<input type="date" {...register('expiryDate')} /></label>
          <label>Issuing Authority<input {...register('issuingAuthority')} /></label>
          <label>File<input type="file" onChange={(e) => setValue('file', e.target.files as FileList)} /></label>
          <button type="submit" disabled={uploadMutation.isPending}>{uploadMutation.isPending ? 'Uploading…' : 'Upload'}</button>
        </form>
      </div>
    </div>
  );
}
